import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Star, Clock, ArrowUp, ArrowDown, ChevronRight, Send, Trash2,
  Undo2, DoorOpen, CheckCircle2, MessageSquareText, Users2, Calendar as CalendarIcon,
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import DatePickerField from "@/components/DatePickerField";

const goldBtn = { backgroundColor: "#F59E0B", color: "#0A0A0A" };

const todayISO = () => {
  const d = new Date();
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yr}-${mo}-${dd}`;
};

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

const stateMeta = {
  waiting: { label: "Waiting", color: "bg-zinc-500/15 border-zinc-500/30 text-zinc-300" },
  arrived: { label: "On deck", color: "bg-amber-400/15 border-amber-400/40 text-amber-300" },
  in_room: { label: "In room", color: "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" },
  done:    { label: "Done",    color: "bg-white/5 border-white/10 text-zinc-500" },
};

export default function AuditionDay() {
  const { productionId, user } = useAuth();
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState({ signups: [], notes: [], counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [noteDraft, setNoteDraft] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    if (!productionId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/audition-day/board", { params: { production_id: productionId, date } });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load audition day");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [productionId, date]);

  // Poll every 15s so admin/staff on other devices see check-ins live.
  useEffect(() => {
    if (!productionId) return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productionId, date]);

  const setState = async (aud, state) => {
    setBusyId(aud.id);
    try {
      const r = await api.post(`/auditions/${aud.id}/checkin-state`, { state });
      setData((d) => ({
        ...d,
        signups: d.signups.map((s) => (s.id === aud.id ? { ...s, ...r.data } : s)),
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally {
      setBusyId("");
      load();
    }
  };

  const move = async (aud, direction) => {
    setBusyId(aud.id);
    try {
      await api.post(`/auditions/${aud.id}/day-position`, { direction });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reorder failed");
    } finally {
      setBusyId("");
    }
  };

  const postNote = async () => {
    const text = noteDraft.trim();
    if (!text) return;
    try {
      const r = await api.post("/audition-day-notes", { date, text }, { params: { production_id: productionId } });
      setData((d) => ({ ...d, notes: [...d.notes, r.data] }));
      setNoteDraft("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Post failed");
    }
  };

  const deleteNote = async (n) => {
    if (!confirm("Delete this note?")) return;
    try {
      await api.delete(`/audition-day-notes/${n.id}`);
      setData((d) => ({ ...d, notes: d.notes.filter((x) => x.id !== n.id) }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const grouped = useMemo(() => {
    const g = { waiting: [], arrived: [], in_room: [], done: [] };
    for (const s of data.signups) {
      const st = s.checkin_state || "waiting";
      (g[st] || g.waiting).push(s);
    }
    return g;
  }, [data.signups]);

  return (
    <div className="space-y-8" data-testid="audition-day-page">
      {/* Header */}
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-gold">Casting · Audition Day</div>
          <h1 className="mt-1 font-display text-4xl md:text-5xl tracking-tight">Check-in board</h1>
          <p className="mt-2 text-sm text-zinc-400 max-w-xl">
            Mark auditioners as they arrive, send them into the room, and keep the audition team
            in sync. Only staff and admins see this board.
          </p>
        </div>
        <div className="w-full sm:w-64">
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-1.5 flex items-center gap-1.5">
            <CalendarIcon className="w-3 h-3" /> Audition date
          </div>
          <DatePickerField testid="audition-day-date" value={date} onChange={setDate} />
        </div>
      </header>

      {/* Summary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Waiting"  n={data.counts?.waiting  || 0} tint="text-zinc-200" />
        <StatCard label="On deck"  n={data.counts?.arrived  || 0} tint="text-amber-300" />
        <StatCard label="In room"  n={data.counts?.in_room  || 0} tint="text-emerald-300" />
        <StatCard label="Done"     n={data.counts?.done     || 0} tint="text-zinc-400" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-6">
        {/* Board */}
        <section className="space-y-6 min-w-0">
          <Column
            title="Waiting"
            icon={<Users2 className="w-4 h-4" />}
            emptyLabel={loading ? "Loading…" : "No auditioners waiting."}
            items={grouped.waiting}
            renderActions={(a, idx) => (
              <>
                <IconBtn
                  testid={`move-up-${a.id}`}
                  onClick={() => move(a, "up")}
                  disabled={idx === 0 || busyId === a.id}
                  Icon={ArrowUp} label="Move up"
                />
                <IconBtn
                  testid={`move-down-${a.id}`}
                  onClick={() => move(a, "down")}
                  disabled={idx === grouped.waiting.length - 1 || busyId === a.id}
                  Icon={ArrowDown} label="Move down"
                />
                <PrimaryBtn
                  testid={`mark-arrived-${a.id}`}
                  onClick={() => setState(a, "arrived")}
                  disabled={busyId === a.id}
                  Icon={CheckCircle2} label="Mark arrived"
                />
              </>
            )}
          />

          <Column
            title="On deck (arrived)"
            icon={<CheckCircle2 className="w-4 h-4" />}
            emptyLabel="No one is on deck yet."
            items={grouped.arrived}
            highlight="border-amber-400/30"
            renderActions={(a) => (
              <>
                <SecondaryBtn
                  testid={`revert-waiting-${a.id}`}
                  onClick={() => setState(a, "waiting")}
                  disabled={busyId === a.id}
                  Icon={Undo2} label="Back to waiting"
                />
                <PrimaryBtn
                  testid={`mark-in-room-${a.id}`}
                  onClick={() => setState(a, "in_room")}
                  disabled={busyId === a.id}
                  Icon={DoorOpen} label="Send in"
                />
              </>
            )}
          />

          <Column
            title="In room"
            icon={<DoorOpen className="w-4 h-4" />}
            emptyLabel="Nobody is auditioning right now."
            items={grouped.in_room}
            highlight="border-emerald-400/30"
            renderActions={(a) => (
              <>
                <SecondaryBtn
                  testid={`revert-arrived-${a.id}`}
                  onClick={() => setState(a, "arrived")}
                  disabled={busyId === a.id}
                  Icon={Undo2} label="Back to on deck"
                />
                <PrimaryBtn
                  testid={`mark-done-${a.id}`}
                  onClick={() => setState(a, "done")}
                  disabled={busyId === a.id}
                  Icon={ChevronRight} label="Mark done"
                />
              </>
            )}
          />

          <Column
            title="Done"
            icon={<Star className="w-4 h-4" />}
            emptyLabel="Nobody has finished yet."
            items={grouped.done}
            dim
            renderActions={(a) => (
              <SecondaryBtn
                testid={`revert-inroom-${a.id}`}
                onClick={() => setState(a, "in_room")}
                disabled={busyId === a.id}
                Icon={Undo2} label="Undo"
              />
            )}
          />
        </section>

        {/* Notes rail (staff/admin only) */}
        <aside className="rounded-2xl border border-white/10 bg-[#0F0F0F] flex flex-col min-h-[520px] lg:sticky lg:top-6 self-start">
          <div className="p-4 border-b border-white/5 flex items-center gap-2">
            <MessageSquareText className="w-4 h-4 text-gold" />
            <div className="font-display text-lg">Team notes</div>
            <span className="ml-auto text-[10px] uppercase tracking-[0.24em] text-zinc-500">
              Staff · Admin only
            </span>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[520px]" data-testid="day-notes-feed">
            {data.notes.length === 0 && (
              <div className="text-xs text-zinc-500 text-center py-10">
                No notes yet. Post an update for the audition team.
              </div>
            )}
            {data.notes.map((n) => (
              <div key={n.id} className="rounded-lg border border-white/10 bg-[#151515] p-3 group">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[11px] text-gold font-medium">{n.author_name}</div>
                  <div className="flex items-center gap-2">
                    <div className="text-[10px] text-zinc-500">{fmtTime(n.created_at)}</div>
                    {(n.author_id === user?.id || user?.role === "org_admin" || user?.role === "super_admin") && (
                      <button
                        data-testid={`delete-note-${n.id}`}
                        onClick={() => deleteNote(n)}
                        className="opacity-0 group-hover:opacity-100 transition text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="mt-1 text-sm text-zinc-200 whitespace-pre-wrap break-words">
                  {n.text}
                </div>
              </div>
            ))}
          </div>
          <div className="p-4 border-t border-white/5 space-y-2">
            <Textarea
              data-testid="day-note-input"
              rows={2}
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) postNote(); }}
              placeholder="Post an update for the audition team… (⌘/Ctrl-Enter to send)"
              className="bg-[#121212] border-white/10 text-sm resize-none"
            />
            <button
              data-testid="post-day-note"
              onClick={postNote}
              disabled={!noteDraft.trim()}
              style={goldBtn}
              className="w-full h-9 rounded font-semibold text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
            >
              <Send className="w-4 h-4" /> Post note
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ---------- Presentational bits ---------- */

function StatCard({ label, n, tint }) {
  return (
    <div className="rounded-lg border border-white/10 bg-[#0F0F0F] p-4">
      <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">{label}</div>
      <div className={`mt-1 text-3xl font-display ${tint}`}>{n}</div>
    </div>
  );
}

function Column({ title, icon, items, emptyLabel, renderActions, highlight, dim }) {
  return (
    <div className={`rounded-2xl border ${highlight || "border-white/10"} bg-[#0F0F0F] overflow-hidden ${dim ? "opacity-70" : ""}`}>
      <div className="p-4 border-b border-white/5 flex items-center gap-2">
        <span className="text-gold">{icon}</span>
        <div className="font-display text-lg">{title}</div>
        <span className="ml-auto text-[10px] uppercase tracking-[0.24em] text-zinc-500">
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="p-6 text-xs text-zinc-500 text-center">{emptyLabel}</div>
      ) : (
        <ul className="divide-y divide-white/5">
          {items.map((a, idx) => (
            <li key={a.id} data-testid={`day-row-${a.id}`} className="p-3 sm:p-4 flex items-center gap-4">
              <Avatar url={a.headshot_url} name={a.name} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-medium truncate">{a.name}</div>
                  <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${stateMeta[a.checkin_state]?.color || stateMeta.waiting.color}`}>
                    {stateMeta[a.checkin_state]?.label || "Waiting"}
                  </span>
                </div>
                <div className="mt-0.5 text-[11px] text-zinc-500 flex items-center gap-2 flex-wrap">
                  {a.slot_start_time && (
                    <span className="inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {fmtTime(a.slot_start_time)}
                    </span>
                  )}
                  {a.arrived_at && <span>Arrived {fmtTime(a.arrived_at)}</span>}
                  {a.in_room_at && <span>· In room {fmtTime(a.in_room_at)}</span>}
                  {a.done_at && <span>· Done {fmtTime(a.done_at)}</span>}
                </div>
                {a.interested_roles?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {a.interested_roles.slice(0, 3).map((r, i) => (
                      <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-zinc-400">{r}</span>
                    ))}
                    {a.interested_roles.length > 3 && (
                      <span className="text-[10px] text-zinc-500">+{a.interested_roles.length - 3}</span>
                    )}
                  </div>
                )}
              </div>
              <div className="flex flex-col sm:flex-row items-end sm:items-center gap-1.5 shrink-0">
                {renderActions(a, idx)}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Avatar({ url, name }) {
  const initials = (name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
  return url ? (
    <img src={url} alt={name} className="w-11 h-11 rounded-full object-cover bg-[#121212] border border-white/10 shrink-0" />
  ) : (
    <div className="w-11 h-11 rounded-full bg-[#121212] border border-white/10 grid place-items-center text-xs font-medium text-zinc-400 shrink-0">
      {initials}
    </div>
  );
}

function IconBtn({ Icon, label, onClick, disabled, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      title={label}
      className="w-8 h-8 rounded border border-white/10 text-zinc-300 hover:bg-white/5 grid place-items-center disabled:opacity-40 disabled:hover:bg-transparent"
    >
      <Icon className="w-3.5 h-3.5" />
    </button>
  );
}

function PrimaryBtn({ Icon, label, onClick, disabled, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      style={goldBtn}
      className="h-8 px-3 rounded font-semibold text-xs inline-flex items-center gap-1 disabled:opacity-40 hover:brightness-110"
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

function SecondaryBtn({ Icon, label, onClick, disabled, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className="h-8 px-2.5 rounded border border-white/10 text-xs text-zinc-300 hover:bg-white/5 inline-flex items-center gap-1 disabled:opacity-40"
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}
